import { useEffect, useMemo, useRef, useState } from 'react';
import { compensationGateway } from '../compensation';
import { peopleGateway } from './index';
import type { EmployeeResponse } from './types';

/** Per-employee list details that the list endpoint does not return (pay type, pay method, primary work location).
 * `failed` means at least one read failed or was refused, so the cell shows an unavailable dash, never a guess. */
export interface EmployeeRowDetails {
  payType: number | null;
  payMethod: number | null;
  workLocationId: string | null;
  failed: boolean;
}

const CONCURRENCY = 5;

function today(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export async function readEmployeeDetails(employeeId: string, signal: AbortSignal): Promise<EmployeeRowDetails> {
  let failed = false;
  let payMethod: number | null = null;
  let workLocationId: string | null = null;
  let payType: number | null = null;
  const [employee, rates] = await Promise.allSettled([
    peopleGateway.getEmployee(employeeId, signal),
    compensationGateway.listWorkRateAssignments(employeeId, signal),
  ]);
  if (employee.status === 'fulfilled') {
    payMethod = employee.value.payMethod ?? null;
    workLocationId = employee.value.primaryWorkLocationId ?? null;
  } else {
    failed = true;
  }
  if (rates.status === 'fulfilled') {
    const now = today();
    const current = rates.value.find(
      (r) => r.isPrimary && r.effectiveStartDate <= now && (r.effectiveEndDate === null || r.effectiveEndDate > now),
    );
    payType = current ? current.payType : null;
  } else {
    failed = true;
  }
  return { payType, payMethod, workLocationId, failed };
}

/**
 * BACKEND GAP: the Employees list returns neither pay type, pay method nor branch, so these are read per employee
 * (employee read plus current primary pay rate) through the existing gateways, a few at a time, only for `employeeIds`.
 * The caller passes the visible page normally, or every employee while a pay type / pay method / branch filter is
 * active. Results are cached by id for the life of the page.
 */
export function useEmployeeListDetails(employees: readonly EmployeeResponse[], employeeIds: readonly string[]) {
  const [details, setDetails] = useState<Record<string, EmployeeRowDetails>>({});
  const settled = useRef(new Set<string>());
  const inFlight = useRef(new Set<string>());
  const known = useMemo(() => new Set(employees.map((e) => e.id)), [employees]);
  const key = employeeIds.filter((id) => known.has(id)).join('|');

  useEffect(() => {
    const flight = inFlight.current;
    const todo = key === '' ? [] : key.split('|').filter((id) => !settled.current.has(id) && !flight.has(id));
    if (todo.length === 0) return;
    const controller = new AbortController();
    const queue = [...todo];
    for (const id of todo) flight.add(id);
    const worker = async () => {
      for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
        const result = await readEmployeeDetails(id, controller.signal);
        if (controller.signal.aborted) return;
        settled.current.add(id);
        flight.delete(id);
        setDetails((prev) => ({ ...prev, [id]: result }));
      }
    };
    void Promise.all(Array.from({ length: Math.min(CONCURRENCY, todo.length) }, worker));
    return () => {
      controller.abort();
      for (const id of todo) flight.delete(id);
    };
  }, [key]);

  const pendingCount = key === '' ? 0 : key.split('|').filter((id) => !(id in details)).length;
  return { details, pendingCount };
}
